import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
    ShieldAlert, Users, Sprout, LayoutGrid, Camera, MessageCircle, BookOpen,
    Share2, CreditCard, RefreshCw, TrendingUp, Sparkles, Leaf,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { adminStats, adminTransactions, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CHART_INK = "#2F392E";
const CHART_SAGE = "#6B8C5C";
const CHART_TERR = "#C06547";
const CHART_GRID = "#D6D1BE";

function Stat({ icon: Icon, label, value, sub, color, testid }) {
    return (
        <div className="journal-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-[var(--vm-sage)] mb-1">
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span className="text-[11px] uppercase tracking-wider">{label}</span>
            </div>
            <div className="font-serif-display text-3xl" style={{ color: color || "var(--vm-text)" }}>
                {value}
            </div>
            {sub && <div className="text-xs text-[var(--vm-text-muted)] mt-1">{sub}</div>}
        </div>
    );
}

function TierBar({ by_tier, total }) {
    const safe = total || 1;
    const segs = [
        { key: "plus",      color: CHART_TERR, label: "Plus" },
        { key: "community", color: CHART_SAGE, label: "Community" },
        { key: "free",      color: "#C7C0A0",  label: "Free" },
    ];
    return (
        <div>
            <div className="flex rounded-full overflow-hidden h-3 bg-[var(--vm-input)]" data-testid="tier-bar">
                {segs.map((s) => {
                    const pct = ((by_tier[s.key] || 0) / safe) * 100;
                    return <div key={s.key} style={{ width: `${pct}%`, background: s.color }} title={`${s.label}: ${by_tier[s.key]}`} />;
                })}
            </div>
            <div className="flex gap-4 mt-2 text-xs text-[var(--vm-text-muted)]">
                {segs.map((s) => (
                    <span key={s.key} className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                        {s.label}: <span className="text-[var(--vm-text)] font-medium">{by_tier[s.key] || 0}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

export default function AdminStats() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [txs, setTxs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user && user !== false && user.role !== "admin") {
            toast.error("Admin only");
            navigate("/");
        }
    }, [user, navigate]);

    const refresh = async () => {
        setLoading(true);
        try {
            const [stats, trans] = await Promise.all([
                adminStats(),
                adminTransactions({ limit: 25 }),
            ]);
            setData(stats);
            setTxs(trans.transactions || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    if (loading || !data) {
        return (
            <div className="space-y-6" data-testid="admin-stats-loading">
                <PageHeader overline="Admin" title="Platform stats" description="Live numbers across the rooteed platform." />
                <p className="text-[var(--vm-text-muted)]">Loading…</p>
            </div>
        );
    }

    const u = data.users;
    const t = data.totals;
    const rev = data.revenue;

    return (
        <div className="space-y-10" data-testid="admin-stats-page">
            <PageHeader
                overline="Admin"
                title="Platform stats"
                description={`Snapshot generated ${new Date(data.generated_at).toLocaleString()}`}
            />

            <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" className="rounded-full border-[var(--vm-border)]">
                    <Link to="/admin" data-testid="admin-users-link"><Users className="w-4 h-4 mr-2" /> Manage users</Link>
                </Button>
                <Button onClick={refresh} variant="outline" className="rounded-full border-[var(--vm-border)]" data-testid="admin-stats-refresh">
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
            </div>

            {/* Users */}
            <section>
                <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-3">Users</h2>
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                    <Stat icon={Users}    label="Total users"  value={u.total}       testid="stat-total-users" />
                    <Stat icon={Sparkles} label="Plus active"  value={u.plus_active} color="var(--vm-terracotta)" testid="stat-plus-active" />
                    <Stat icon={Leaf}     label="Community"    value={u.community}   color="var(--vm-sage)" testid="stat-community" />
                    <Stat icon={TrendingUp} label="New — 30 d" value={u.new_30d}     sub={`${u.new_7d} in last 7 days`} testid="stat-new-30d" />
                </div>
                <div className="journal-card p-5">
                    <h3 className="text-sm uppercase tracking-wider text-[var(--vm-sage)] mb-3">By tier</h3>
                    <TierBar by_tier={u.by_tier} total={u.total} />
                </div>
            </section>

            {/* Revenue */}
            <section>
                <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-3">Revenue</h2>
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                    <Stat
                        icon={CreditCard}
                        label="Total revenue"
                        value={`€${rev.grand_total_eur.toFixed(2)}`}
                        testid="stat-revenue-total"
                        color="var(--vm-terracotta)"
                    />
                    <Stat
                        icon={CreditCard}
                        label="Transactions"
                        value={rev.transaction_count}
                        sub={`${data.pending_transactions} pending`}
                        testid="stat-revenue-tx"
                    />
                    <Stat
                        icon={CreditCard}
                        label="Stripe"
                        value={`€${(rev.by_provider.stripe?.total_eur || 0).toFixed(2)}`}
                        sub={`${rev.by_provider.stripe?.count || 0} payments`}
                        testid="stat-revenue-stripe"
                    />
                    <Stat
                        icon={CreditCard}
                        label="PayPal"
                        value={`€${(rev.by_provider.paypal?.total_eur || 0).toFixed(2)}`}
                        sub={`${rev.by_provider.paypal?.count || 0} payments`}
                        testid="stat-revenue-paypal"
                    />
                </div>
                <div className="journal-card p-5">
                    <h3 className="text-sm uppercase tracking-wider text-[var(--vm-sage)] mb-3">Revenue — last 30 days</h3>
                    <div className="h-56" data-testid="revenue-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.revenue_series_30d}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                                <XAxis dataKey="date" tick={{ fill: CHART_INK, fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                                <YAxis tick={{ fill: CHART_INK, fontSize: 10 }} />
                                <Tooltip
                                    contentStyle={{ background: "var(--vm-card)", border: "1px solid var(--vm-border)", borderRadius: 6 }}
                                    formatter={(v, n) => [n === "total" ? `€${v}` : v, n === "total" ? "Revenue" : "Count"]}
                                />
                                <Bar dataKey="total" fill={CHART_TERR} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </section>

            {/* Growth */}
            <section>
                <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-3">Growth</h2>
                <div className="journal-card p-5">
                    <h3 className="text-sm uppercase tracking-wider text-[var(--vm-sage)] mb-3">Daily signups — last 30 days</h3>
                    <div className="h-56" data-testid="signup-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.signup_series_30d}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
                                <XAxis dataKey="date" tick={{ fill: CHART_INK, fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                                <YAxis tick={{ fill: CHART_INK, fontSize: 10 }} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ background: "var(--vm-card)", border: "1px solid var(--vm-border)", borderRadius: 6 }}
                                />
                                <Line type="monotone" dataKey="count" stroke={CHART_SAGE} strokeWidth={2} dot={{ r: 2, fill: CHART_SAGE }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section>
                <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-3">Platform content</h2>
                <div className="grid md:grid-cols-4 gap-4">
                    <Stat icon={Sprout}     label="Gardens"      value={t.gardens}        testid="stat-gardens" />
                    <Stat icon={LayoutGrid} label="Beds"         value={t.beds}           testid="stat-beds" />
                    <Stat icon={Leaf}       label="Crops"        value={t.crops}          testid="stat-crops" />
                    <Stat icon={Camera}     label="Photos"       value={t.images}         testid="stat-images" />
                    <Stat icon={BookOpen}   label="Book notes"   value={t.book_notes}     testid="stat-book-notes" />
                    <Stat icon={MessageCircle} label="AI msgs"   value={t.chat_messages}  sub={`${data.chat_30d.messages_30d} last 30 d`} testid="stat-chat-msgs" />
                    <Stat icon={Share2}     label="Share links"  value={t.share_tokens}   testid="stat-shares" />
                    <Stat icon={MessageCircle} label="Chat users (30 d)" value={data.chat_30d.active_users_30d} testid="stat-chat-users" />
                </div>
            </section>

            {/* Transactions */}
            <section>
                <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-3">Latest 25 transactions</h2>
                <div className="journal-card overflow-x-auto" data-testid="admin-transactions-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[var(--vm-input)] text-[var(--vm-text-muted)] uppercase text-[10px] tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-2">Date</th>
                                <th className="text-left px-4 py-2">User</th>
                                <th className="text-left px-4 py-2">Provider</th>
                                <th className="text-right px-4 py-2">Amount</th>
                                <th className="text-left px-4 py-2">Status</th>
                                <th className="text-center px-4 py-2">Applied</th>
                            </tr>
                        </thead>
                        <tbody>
                            {txs.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-4 text-center text-[var(--vm-text-muted)]">No payments yet.</td></tr>
                            ) : txs.map((tx) => (
                                <tr key={tx.session_id + tx.created_at} className="border-t border-[var(--vm-border)]">
                                    <td className="px-4 py-2 text-[var(--vm-text-muted)]">{new Date(tx.created_at).toLocaleString()}</td>
                                    <td className="px-4 py-2">{tx.user_email || tx.user_id?.slice(0, 8)}</td>
                                    <td className="px-4 py-2 uppercase text-[11px] tracking-wider">{tx.provider || "stripe"}</td>
                                    <td className="px-4 py-2 text-right font-mono">€{(tx.amount || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2">{tx.payment_status}</td>
                                    <td className="px-4 py-2 text-center">{tx.applied ? "✅" : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <footer className="text-xs text-[var(--vm-text-muted)] flex items-center gap-2 pt-4">
                <ShieldAlert className="w-3 h-3" />
                Admin view — data visible only to users with role=admin.
            </footer>
        </div>
    );
}
